@if (displayMode() === 'table') {
  <div class="table-container">
    @if (useVirtualScroll()) {
      <div class="joii-table-virtual-frame" [style.height]="virtualScrollHeight()">
        @if (showHeader()) {
          <div #headerScrollElement class="joii-table-virtual-header">
            <table class="joii-table joii-table--virtual-sync" [style.width.px]="virtualTableWidth()">
              <ng-container *ngTemplateOutlet="tableHead; context: { virtual: true }"></ng-container>
            </table>
          </div>
        }
        <div #scrollElement class="joii-table-scroll-container">
          <div [style.height.px]="virtualizer.getTotalSize()">
            <table class="joii-table joii-table--virtual-sync" [style.width.px]="virtualTableWidth()">
              <ng-container *ngTemplateOutlet="virtualRows"></ng-container>
            </table>
          </div>
          @if (loading()) {
            <ng-container *ngTemplateOutlet="loadingIndicator"></ng-container>
          }
          @if (canLoadMore()) {
            <div #scrollSentinel class="joii-table-scroll-sentinel" aria-hidden="true"></div>
          }
        </div>
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="standardTable"></ng-container>
      @if (loading()) {
        <ng-container *ngTemplateOutlet="loadingIndicator"></ng-container>
      }
    }
  </div>

  <ng-template #tableHead let-virtual="virtual">
    <thead>
      @for (headerGroup of table.getHeaderGroups(); track headerGroup.id; let first = $first) {
        <tr>
          @if (enableRowSelection() && first) {
            <th
              class="joii-table-select-cell"
              [attr.rowspan]="table.getHeaderGroups().length"
              [style.width.px]="virtual ? selectColumnWidth : null"
            >
              <div class="joii-table-cell-inner joii-table-header-cell-inner">
                <joii-checkbox
                  aria-label="Select all rows"
                  [checked]="table.getIsAllRowsSelected()"
                  [indeterminate]="table.getIsSomeRowsSelected()"
                  (checkedChange)="toggleAllRows($event)"
                  (click)="$event.stopPropagation()"
                />
              </div>
            </th>
          }
          @for (header of headerGroup.headers; track header.id) {
            @if (!header.isPlaceholder) {
              <th
                [attr.colspan]="header.colSpan"
                [attr.rowspan]="header.rowSpan"
                [style.width.px]="virtual ? header.getSize() : null"
                [style]="getColumnStyle(header.column, !virtual)"
                [class.joii-table-cell--pinned]="!!header.column.getIsPinned()"
                [class.joii-table-cell--pinned-left-last]="isLastLeftPinnedColumn(header.column)"
                [class.joii-table-cell--pinned-right-first]="isFirstRightPinnedColumn(header.column)"
                [class.joii-table-header--sortable]="header.column.getCanSort()"
                [class.joii-table-cell--wrap]="isWrapColumn(header.column)"
                (click)="handleHeaderClick(header.column, $event)"
              >
                <div class="joii-table-cell-inner joii-table-header-cell-inner">
                  <ng-template [flexRender]="header.column.columnDef.header" [flexRenderProps]="header.getContext()">
                    <p joii-text [size]="4" weight="semibold">{{ getHeaderLabel(header) }}</p>
                  </ng-template>
                  @if (header.column.getCanSort()) {
                    <span class="joii-table-sort-indicator" [attr.data-sort]="header.column.getIsSorted() || 'none'">
                      @switch (header.column.getIsSorted()) {
                        @case ('asc') {
                          <svg [lucideIcon]="chevronUpIcon" />
                        }
                        @case ('desc') {
                          <svg [lucideIcon]="chevronDownIcon" />
                        }
                      }
                    </span>
                  }
                  @if (getColumnFilter(header.column); as filter) {
                    <joii-table-column-filter
                      [config]="filter"
                      [value]="filterValues()[header.column.id]"
                      [label]="getHeaderLabel(header)"
                      (valueChange)="onFilterChange(header.column.id, $event)"
                      (click)="$event.stopPropagation()"
                    />
                  }
                  @if (enableColumnPinning() && header.column.getCanPin()) {
                    <button
                      type="button"
                      class="joii-table-pin-button"
                      [attr.aria-label]="header.column.getIsPinned() ? 'Unpin column' : 'Pin column'"
                      (click)="toggleColumnPin(header.column, $event)"
                    >
                      <svg [lucideIcon]="header.column.getIsPinned() ? pinOffIcon : pinIcon" [size]="14" />
                    </button>
                  }
                </div>
              </th>
            }
          }
        </tr>
      }
    </thead>
  </ng-template>

  <ng-template #virtualRows>
    <tbody>
      @for (virtualRow of virtualizer.getVirtualItems(); track virtualRow.index; let renderedIndex = $index) {
        @let row = rows()[virtualRow.index];
        <tr
          [class.joii-table-row--highlighted]="isRowHighlighted(getOriginalRowIndex(row))"
          [class.joii-table-row--error-highlighted]="isRowErrorHighlighted(getOriginalRowIndex(row))"
          [style.height.px]="virtualRow.size"
          [style.transform]="getVirtualRowTransform(virtualRow, renderedIndex)"
          (click)="handleRowClick(row)"
        >
          @if (enableRowSelection()) {
            <ng-container *ngTemplateOutlet="selectCell; context: { row, virtual: true }"></ng-container>
          }
          @for (cell of row.getVisibleCells(); track cell.id) {
            <td
              [style.width.px]="cell.column.getSize()"
              [style]="getPinningOffset(cell.column)"
              [class.joii-table-cell--pinned]="!!cell.column.getIsPinned()"
              [class.joii-table-cell--pinned-left-last]="isLastLeftPinnedColumn(cell.column)"
              [class.joii-table-cell--pinned-right-first]="isFirstRightPinnedColumn(cell.column)"
              [class.joii-table-cell--wrap]="isWrapColumn(cell.column)"
            >
              <div class="joii-table-cell-inner">
                <ng-template [flexRender]="cell.column.columnDef.cell" [flexRenderProps]="cell.getContext()">
                  {{ cell.renderValue() }}
                </ng-template>
              </div>
            </td>
          }
        </tr>
      }
    </tbody>
  </ng-template>

  <ng-template #standardTable>
    <table class="joii-table">
      @if (showHeader()) {
        <ng-container *ngTemplateOutlet="tableHead; context: { virtual: false }"></ng-container>
      }
      <tbody>
        @for (row of table.getRowModel().rows; track row.id) {
          <tr
            [class.joii-table-row--highlighted]="isRowHighlighted(getOriginalRowIndex(row))"
            [class.joii-table-row--error-highlighted]="isRowErrorHighlighted(getOriginalRowIndex(row))"
            (click)="handleRowClick(row)"
          >
            @if (enableRowSelection()) {
              <ng-container *ngTemplateOutlet="selectCell; context: { row, virtual: false }"></ng-container>
            }
            @for (cell of row.getVisibleCells(); track cell.id) {
              <td
                [style.width]="
                  cell.column.getSize() !== 150 || isWrapColumn(cell.column) ? cell.column.getSize() + 'px' : null
                "
                [style]="getPinningOffset(cell.column)"
                [class.joii-table-cell--pinned]="!!cell.column.getIsPinned()"
                [class.joii-table-cell--pinned-left-last]="isLastLeftPinnedColumn(cell.column)"
                [class.joii-table-cell--pinned-right-first]="isFirstRightPinnedColumn(cell.column)"
                [class.joii-table-cell--wrap]="isWrapColumn(cell.column)"
              >
                <div class="joii-table-cell-inner">
                  <ng-template [flexRender]="cell.column.columnDef.cell" [flexRenderProps]="cell.getContext()">
                    {{ cell.renderValue() }}
                  </ng-template>
                </div>
              </td>
            }
          </tr>
        }
      </tbody>
    </table>
    @if (showEmptyState()) {
      <ng-container *ngTemplateOutlet="emptyState"></ng-container>
    }
  </ng-template>

  <ng-template #selectCell let-row="row" let-virtual="virtual">
    <td class="joii-table-select-cell" [style.width.px]="virtual ? selectColumnWidth : null">
      <div class="joii-table-cell-inner">
        <joii-checkbox
          aria-label="Select row"
          [checked]="row.getIsSelected()"
          [disabled]="!row.getCanSelect()"
          (checkedChange)="toggleRow(row, $event)"
          (click)="$event.stopPropagation()"
        />
      </div>
    </td>
  </ng-template>
} @else {
  <div class="cards-container">
    @for (row of table.getRowModel().rows; track row.id) {
      <div class="joii-table-card">
        @if (cardHeaderTemplate(); as template) {
          <ng-container *ngTemplateOutlet="template; context: { row }"></ng-container>
        }
        @if (cardContentTemplate(); as template) {
          <ng-container *ngTemplateOutlet="template; context: { row }"></ng-container>
        }
        @if (cardFooterTemplate(); as template) {
          <ng-container *ngTemplateOutlet="template; context: { row }"></ng-container>
        }
      </div>
    }
    @if (loading()) {
      <ng-container *ngTemplateOutlet="loadingIndicator"></ng-container>
    }
    @if (showEmptyState()) {
      <ng-container *ngTemplateOutlet="emptyState"></ng-container>
    }
  </div>
}

<ng-template #emptyState>
  <div class="joii-table-empty" role="status">
    @if (emptyStateTemplate(); as template) {
      <ng-container *ngTemplateOutlet="template"></ng-container>
    } @else {
      <p joii-text [size]="4" color="tint2">{{ emptyMessage() }}</p>
    }
  </div>
</ng-template>

<ng-template #loadingIndicator>
  <div class="joii-table-loading" role="status" aria-live="polite">
    <joii-loader [size]="24" ariaLabel="Loading more rows" />
  </div>
</ng-template>

@if (paginationType() === 'paginator' && totalPages() > 1) {
  <div class="joii-table-pagination">
    <joii-pagination variant="Numbers" [pageCount]="totalPages()" [(page)]="page"></joii-pagination>
  </div>
}
@if (canLoadMore() && !useVirtualScroll()) {
  <div #scrollSentinel class="joii-table-scroll-sentinel" aria-hidden="true"></div>
}
